import { PhClockCountdown, PhWarningCircle } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import type { TasProgramTemplateExtension } from 'tio-data/data/resources/tas-program-template.ext';
import {
  getEligibilityCountsRequest,
  getParticipationCountsRequest,
} from 'tio-data/requests/company';
import { executeSearchRequest } from 'tio-data/requests/search';
import type PartnerService from 'tio-employee/services/partner';
import type {
  AttentionItem,
  AttentionSource,
  DashboardContext,
} from 'tio-employee/utils/partner-dashboard/registry';

/**
 * Gate for every TA widget and source: account owners and partner admins
 * exist at companies without tuition assistance, where these would be empty.
 */
export const hasTuitionAssistance = (ctx: DashboardContext) =>
  (ctx.owner.lookup('service:partner') as PartnerService).hasNewTuitionAssistance;

/** Matches the API's `SLA_AT_RISK_DAYS`: more than this many whole days waiting is at risk. */
export const SLA_AT_RISK_DAYS = 14;

/** Matches the API's `SLA_THRESHOLD_DAYS`: more than this many whole days waiting has breached. */
export const SLA_BREACHED_DAYS = 28;

/** The pulse compares this many trailing days with the same span before it. */
export const PULSE_PERIOD_DAYS = 30;

/** How many weeks the trend chart covers, including the current one. */
export const TREND_WEEKS = 12;

/** The two states an application waits in for a partner reviewer. */
export const PENDING_STAGES = {
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': 'courses',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': 'evidence',
} as const;

export type PendingStage = (typeof PENDING_STAGES)[keyof typeof PENDING_STAGES];

const DAY_MS = 24 * 60 * 60 * 1000;
const STATE_FIELD = 'tas_application_state_keyword';
const LAST_CHANGE_FIELD = 'tas_application_latest_state_change_at_date';
const SUBMITTED_FIELD = 'tas_application_courses_submitted_timestamp_date';
const REVIEWED_FIELD = 'tas_application_courses_latest_review_timestamp_date';
const OUTCOME_FIELD = 'tas_application_courses_latest_review_outcome_keyword';
const PAID_FIELD = 'tas_application_payment_paid_amount_integer';
const TEMPLATE_FIELD = 'tas_program_template_id_keyword';

// Aggregation names are single words: the API snake-cases request keys and may camel-case them back.
interface Bucket {
  key?: string | number;
  doc_count?: number;
  docCount?: number;
  [aggregation: string]: unknown;
}

type Aggregations = Record<string, unknown>;

const docCount = (bucket: unknown) => {
  const b = bucket as Bucket | undefined;
  return b?.doc_count ?? b?.docCount ?? 0;
};

const bucketsOf = (aggregation: unknown) =>
  ((aggregation as { buckets?: Bucket[] } | undefined)?.buckets ?? []).filter(Boolean);

const metricOf = (aggregation: unknown) => {
  const value = (aggregation as { value?: number | null } | undefined)?.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const pendingFilter = { terms: { [STATE_FIELD]: Object.keys(PENDING_STAGES) } };

/**
 * A size-0 search over the company's TAS applications, for aggregations only.
 * The API requires the company filter for partner roles; the record-type
 * filter keeps program instances, which share the index, out of the counts.
 */
async function aggregateApplications(
  ctx: DashboardContext,
  aggs: Record<string, unknown>,
  filters: Record<string, unknown>[] = []
) {
  const store = ctx.owner.lookup('service:store') as Store;
  const { content } = await store.request<{ aggregations?: Aggregations }>(
    executeSearchRequest({
      'model-type': 'TasApplication',
      'search-body': {
        size: 0,
        query: {
          bool: {
            filter: [
              { term: { company_id: ctx.companyId } },
              { term: { record_type: 'application' } },
              ...filters,
            ],
          },
        },
        aggs,
      },
    })
  );
  return content.aggregations ?? {};
}

// The API truncates elapsed time to whole days, so "more than 28 days" means 29 or more elapsed.
const pastWholeDays = (days: number) => `now-${days + 1}d`;

const memo = <T>(load: (ctx: DashboardContext) => Promise<T>) => {
  const cache = new WeakMap<DashboardContext, Promise<T>>();
  return (ctx: DashboardContext) => {
    let pending = cache.get(ctx);
    if (!pending) {
      pending = load(ctx);
      cache.set(ctx, pending);
    }
    return pending;
  };
};

/** Company-wide TA activity for the hero tiles. */
export interface ProgramPulse {
  /** Applications whose courses were submitted in the last `PULSE_PERIOD_DAYS` days. */
  submitted: number;
  /** The same count for the period before that, for the direction of change. */
  submittedBefore: number;
  /** Applications waiting on a reviewer right now. */
  inReview: number;
  /** Cents paid across every application to date. */
  paidCents: number;
}

/**
 * One aggregation search for the pulse tiles, memoized per route visit so
 * every tile shares it. Submission dates are the latest submission, so a
 * resubmitted application counts in the period it was resubmitted.
 */
export const loadProgramPulse = memo(async (ctx): Promise<ProgramPulse> => {
  if (!ctx.companyId) return { submitted: 0, submittedBefore: 0, inReview: 0, paidCents: 0 };
  const recent = `now-${PULSE_PERIOD_DAYS}d`;
  const before = `now-${PULSE_PERIOD_DAYS * 2}d`;
  const aggregations = await aggregateApplications(ctx, {
    submitted: { filter: { range: { [SUBMITTED_FIELD]: { gte: recent } } } },
    previous: { filter: { range: { [SUBMITTED_FIELD]: { gte: before, lt: recent } } } },
    pending: { filter: pendingFilter },
    paid: { sum: { field: PAID_FIELD } },
  });
  return {
    submitted: docCount(aggregations['submitted']),
    submittedBefore: docCount(aggregations['previous']),
    inReview: docCount(aggregations['pending']),
    paidCents: metricOf(aggregations['paid']) ?? 0,
  };
});

/**
 * Employees with an active TA participant record at the company.
 *
 * @throws when the API refuses the company counts (it requires company show access)
 */
export async function loadActiveParticipants(ctx: DashboardContext) {
  if (!ctx.companyId) return 0;
  const store = ctx.owner.lookup('service:store') as Store;
  const { content } = await store.request<{ data?: Record<string, number> }>(
    getParticipationCountsRequest(ctx.companyId)
  );
  return content.data?.['TAS.ParticipantState.ACTIVE'] ?? 0;
}

/** Pending applications past an SLA mark, per approval stage. */
export interface SlaStageCounts {
  stage: PendingStage;
  breached: number;
  atRisk: number;
  /** Epoch ms of the longest-waiting application's last state change in each group. */
  oldestBreached: number | null;
  oldestAtRisk: number | null;
}

/**
 * Pending applications grouped by stage and SLA mark, measured at query time
 * from each application's last state change, the same clock the API's
 * `sla_status` uses. The indexed `sla_status` is not used: it is computed when
 * a document is indexed, so an application left untouched never ages past it.
 */
export async function loadSlaCounts(ctx: DashboardContext): Promise<SlaStageCounts[]> {
  if (!ctx.companyId) return [];
  const breachedBy = pastWholeDays(SLA_BREACHED_DAYS);
  const atRiskBy = pastWholeDays(SLA_AT_RISK_DAYS);
  const oldest = { oldest: { min: { field: LAST_CHANGE_FIELD } } };
  const aggregations = await aggregateApplications(
    ctx,
    {
      stages: {
        terms: { field: STATE_FIELD, size: Object.keys(PENDING_STAGES).length },
        aggs: {
          breached: {
            filter: { range: { [LAST_CHANGE_FIELD]: { lte: breachedBy } } },
            aggs: oldest,
          },
          risk: {
            filter: { range: { [LAST_CHANGE_FIELD]: { gt: breachedBy, lte: atRiskBy } } },
            aggs: oldest,
          },
        },
      },
    },
    [pendingFilter]
  );
  return bucketsOf(aggregations['stages']).flatMap((bucket) => {
    const stage = PENDING_STAGES[bucket.key as keyof typeof PENDING_STAGES];
    if (!stage) return [];
    const breached = bucket['breached'] as Bucket | undefined;
    const risk = bucket['risk'] as Bucket | undefined;
    return [
      {
        stage,
        breached: docCount(breached),
        atRisk: docCount(risk),
        oldestBreached: metricOf(breached?.['oldest']),
        oldestAtRisk: metricOf(risk?.['oldest']),
      },
    ];
  });
}

/**
 * Needs Your Attention rows for applications past an SLA mark, one per stage
 * and mark. Each row's deadline is when its longest-waiting application
 * breached (or will breach) the SLA, so breached rows sort first. Rows open
 * the approval queue showing every pending item, not only the viewer's.
 */
export const SLA_BREACHES_SOURCE: AttentionSource = {
  id: 'sla-breaches',
  drivenBy: ['accountOwner', 'partnerAdmin'],
  requires: hasTuitionAssistance,
  load: async (ctx) => {
    const intl = ctx.owner.lookup('service:intl') as IntlService;
    const counts = await loadSlaCounts(ctx);
    const action = {
      label: intl.t('partner_dashboard.sla.view_action'),
      route: 'authenticated.admin.tas.approval-queue',
      // Query params are sticky, so every queue filter is reset, not only the two that matter.
      query: {
        disableReviewer: true,
        order: 'first_submitted_asc',
        approval: '',
        program: '',
        search: '',
        waitingOnOthers: false,
        page: 1,
      },
    };
    const rows: AttentionItem[] = [];
    for (const { stage, breached, atRisk, oldestBreached, oldestAtRisk } of counts) {
      const stageName = intl.t(`partner_dashboard.sla.stage_${stage}`);
      const due = (oldest: number | null) =>
        oldest === null
          ? {}
          : { dueAt: new Date(oldest + (SLA_BREACHED_DAYS + 1) * DAY_MS).toISOString() };
      if (breached) {
        rows.push({
          id: `sla-breaches:${stage}:breached`,
          title: intl.t('partner_dashboard.sla.breached_title', {
            count: breached,
            stage: stageName,
            days: SLA_BREACHED_DAYS,
          }),
          description: intl.t('partner_dashboard.sla.breached_description', {
            days: SLA_BREACHED_DAYS,
          }),
          icon: PhWarningCircle,
          urgency: 'urgent',
          ...due(oldestBreached),
          dueLabel: intl.t('partner_dashboard.sla.breached_label'),
          action,
        });
      }
      if (atRisk) {
        rows.push({
          id: `sla-breaches:${stage}:at-risk`,
          title: intl.t('partner_dashboard.sla.at_risk_title', { count: atRisk, stage: stageName }),
          description: intl.t('partner_dashboard.sla.at_risk_description', {
            days: SLA_AT_RISK_DAYS,
            limit: SLA_BREACHED_DAYS,
          }),
          icon: PhClockCountdown,
          urgency: 'soon',
          ...due(oldestAtRisk),
          dueLabel: intl.t('partner_dashboard.sla.at_risk_label'),
          action,
        });
      }
    }
    return rows;
  },
};

/** One week of the trend chart. */
export interface TrendWeek {
  /** Epoch ms of the week's first day. */
  weekStart: number;
  submitted: number;
  approved: number;
}

/**
 * Courses submitted and courses approved per week over the last `TREND_WEEKS`
 * weeks, oldest first, with empty weeks filled in. Weeks start Monday 00:00 UTC. "Approved" is the latest
 * courses review landing as an approval, so an application approved and then
 * reopened counts in the week of its latest review.
 */
export async function loadTrends(ctx: DashboardContext): Promise<TrendWeek[]> {
  if (!ctx.companyId) return [];
  const from = `now-${TREND_WEEKS - 1}w/w`;
  const weekly = (field: string) => ({
    date_histogram: {
      field,
      calendar_interval: 'week',
      min_doc_count: 0,
      extended_bounds: { min: from, max: 'now/w' },
    },
  });
  const aggregations = await aggregateApplications(ctx, {
    submitted: {
      filter: { range: { [SUBMITTED_FIELD]: { gte: from } } },
      aggs: { weeks: weekly(SUBMITTED_FIELD) },
    },
    approved: {
      filter: {
        bool: {
          filter: [
            { range: { [REVIEWED_FIELD]: { gte: from } } },
            { term: { [OUTCOME_FIELD]: 'approve' } },
          ],
        },
      },
      aggs: { weeks: weekly(REVIEWED_FIELD) },
    },
  });
  const weeks = new Map<number, TrendWeek>();
  const add = (aggregation: unknown, field: 'submitted' | 'approved') => {
    for (const bucket of bucketsOf((aggregation as Bucket | undefined)?.['weeks'])) {
      const weekStart = Number(bucket.key);
      if (!Number.isFinite(weekStart)) continue;
      const week = weeks.get(weekStart) ?? { weekStart, submitted: 0, approved: 0 };
      week[field] = docCount(bucket);
      weeks.set(weekStart, week);
    }
  };
  add(aggregations['submitted'], 'submitted');
  add(aggregations['approved'], 'approved');
  return [...weeks.values()].sort((a, b) => a.weekStart - b.weekStart).slice(-TREND_WEEKS);
}

/** A launched program with how many employees it covers and how much is in flight. */
export interface ProgramSummary {
  id: string;
  name: string;
  /** Employees with an active eligibility for the program; null when the counts didn't load. */
  eligible: number | null;
  /** Applications on the program waiting on a reviewer. */
  inReview: number;
}

// Comfortably above any company's launched-program count; buckets beyond it would read as zero.
const PROGRAM_BUCKETS = 500;

// Eligibility count keys are the API's stringified group tuple: `["CODE", 12, "TAS.EligibilityState.ACTIVE"]`.
function activeEligibilityByTemplate(data: Record<string, number> | undefined) {
  const counts = new Map<string, number>();
  for (const [key, value] of Object.entries(data ?? {})) {
    const parts = Array.from(key.matchAll(/"(.*?)"|(\d+)/g), (m) => m[1] ?? m[2]);
    const [, templateId, state] = parts;
    if (templateId && state === 'TAS.EligibilityState.ACTIVE') {
      counts.set(templateId, (counts.get(templateId) ?? 0) + value);
    }
  }
  return counts;
}

/**
 * The company's launched TA programs with eligibility and in-review counts.
 * The eligibility counts are best-effort: if they fail the programs still list.
 */
export async function loadPrograms(ctx: DashboardContext): Promise<ProgramSummary[]> {
  if (!ctx.companyId) return [];
  const store = ctx.owner.lookup('service:store') as Store;
  const [templates, eligibility, pending] = await Promise.all([
    store.request<{ data: TasProgramTemplateExtension[] }>(
      query<TasProgramTemplateExtension>('tas-program-template', {
        'filter[state]': 'TAS.ProgramTemplateState.LAUNCHED',
        'filter[company]': ctx.companyId,
        'filter[isTrial]': 'true,false',
      })
    ),
    store
      .request<{ data?: Record<string, number> }>(getEligibilityCountsRequest(ctx.companyId))
      .then(({ content }) => activeEligibilityByTemplate(content.data))
      .catch(() => null),
    aggregateApplications(
      ctx,
      { programs: { terms: { field: TEMPLATE_FIELD, size: PROGRAM_BUCKETS } } },
      [pendingFilter]
    ),
  ]);
  const inReview = new Map(
    bucketsOf(pending['programs']).map((bucket) => [String(bucket.key), docCount(bucket)])
  );
  return templates.content.data.map((template) => {
    const id = String(template.id);
    return {
      id,
      name: template.programName,
      eligible: eligibility ? (eligibility.get(id) ?? 0) : null,
      inReview: inReview.get(id) ?? 0,
    };
  });
}
